import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';

/**
 * Tracks the current pathname so non-router-aware code (e.g. design-system
 * leaf components like `Avatar`) can ask "are we on an admin route?" without
 * needing to call `useLocation()` itself — which would crash any component
 * tree mounted outside a `<Router>` (sandboxes, isolated unit tests, etc.).
 *
 * `AdminRouteTracker` lives inside the router and pushes the pathname into
 * this store on every navigation. `useIsAdminRoute()` then becomes a pure
 * store read with no router dependency.
 */
interface AdminRouteStore {
	pathname: string;
}

const useAdminRouteStore = create<AdminRouteStore>(() => ({
	pathname: typeof window !== 'undefined' ? window.location.pathname : '/',
}));

const ADMIN_PREFIXES = ['/admin', '/super_admin'];

const isAdminPath = (pathname: string): boolean => ADMIN_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

/**
 * Mount once inside the router (via `RootLayout`) to keep the admin-route
 * store in sync with React Router's location.
 */
export const AdminRouteTracker = (): null => {
	const { pathname } = useLocation();
	useEffect(() => {
		useAdminRouteStore.setState({ pathname });
	}, [pathname]);
	return null;
};

/**
 * Returns true when the current route is under `/admin/*` or `/super_admin/*`
 * (or equals `/admin` / `/super_admin`).
 *
 * Used by the seasonal theme to suppress fun ornaments — falling petals,
 * dynamic landscape background, avatar wreaths — on admin and super-admin
 * pages so those experiences stay business-focused. The user's color-mode preference
 * and the seasonal color tokens still apply (so admin pages will reflect
 * the seasonal palette and time-of-day color mode); only the visual
 * "personality" pieces are scoped out.
 *
 * Safe to call outside a router (will report `false` until the tracker mounts).
 */
export const useIsAdminRoute = (): boolean => useAdminRouteStore((s) => isAdminPath(s.pathname));
